import { useEffect, useState } from 'react';
import { Composer, type MicSignal } from '../components/Composer';
import { Icon } from '../components/Icon';
import { Waveform, type WaveMode } from '../components/Waveform';
import { engineLabels, useVoiceStatus } from '../lib/listen';
import { session } from '../lib/session';
import { useWake, wakeLabel } from '../lib/useWake';
import { canWake } from '../lib/wake';
import { greeting } from '@elena/shared';
import { useElena } from '../state';
import { ElenaAvatar } from '../components/ElenaAvatar';
import { RevealText } from '../components/RevealText';

/**
 * The PC widget (#/widget): a small window the desktop agent opens at start-up.
 * It always listens for "Hej Elena" and shows only the conversation essentials.
 */
export function Widget({
  micSignal,
  onMic,
}: {
  micSignal: MicSignal;
  onMic: (seed?: unknown, fromWake?: boolean) => void;
}) {
  const { messages, thinking, spokenTurn, connection, error } = useElena();
  const wake = useWake(onMic, true);
  const voice = useVoiceStatus();
  const [interim, setInterim] = useState<string | null>(null);

  // The widget runs on the PC itself: commands from here get the PC's trust level.
  useEffect(() => {
    session.setOrigin('pc');
    document.title = 'Elena · Hej Elena';
    document.body.classList.add('is-widget');
    return () => document.body.classList.remove('is-widget');
  }, []);

  const lastElena = [...messages].reverse().find((m) => m.role === 'elena');
  const lastUser = [...messages].reverse().find((m) => m.role === 'user');
  const mode: WaveMode =
    interim !== null
      ? 'listening'
      : thinking
        ? 'thinking'
        : wake.state === 'listening'
          ? 'idle'
          : lastElena && lastElena.id !== spokenTurn
            ? 'speaking'
            : 'idle';

  return (
    <main className="widget" aria-live="polite">
      <header className="widget-head">
        <span className="brand-mark">
          <Icon name="wave" size={20} stroke={2} />
        </span>
        <span className="brand-name">Elena</span>
        <span className="push row gap-8 small">
          <span
            className={`dot ${connection !== 'online' ? 'off' : wake.state === 'listening' ? 'on' : 'warn'}`}
          />
          {connection !== 'online'
            ? 'nema veze'
            : canWake()
              ? `Hej Elena · ${wakeLabel(wake)}`
              : 'glas samo u Chrome/Edge'}
        </span>
      </header>

      <section className="widget-body">
        <ElenaAvatar mode={mode} size="compact" />
        <div className="widget-copy">
          {interim !== null ? (
            <p className="widget-live">{interim || 'Slušam…'}</p>
          ) : (
            <>
              {lastUser && <p className="console-user">{lastUser.text}</p>}
              <RevealText
                className="widget-reply"
                text={thinking ? 'Razmišljam…' : (lastElena?.text ?? greeting())}
              />
            </>
          )}
          <Waveform mode={mode} />
          {interim === null && wake.heard && (
            <p className="muted small">Čula sam: „{wake.heard}“</p>
          )}
          {wake.issue && (
            <p className="muted small">Prepoznavanje govora: {wake.issue} (pokušavam ponovo)</p>
          )}
          {wake.state === 'error' && wake.message && <p className="notice">{wake.message}</p>}
          {error && <p className="notice">{error}</p>}
          {voice.fallback && <p className="muted small">{voice.fallback}</p>}
        </div>
      </section>

      <footer className="widget-foot">
        <p className="muted small">
          Reci „Hej Elena, …“ ili pritisni mikrofon. Razume: {engineLabels[voice.engine]}.
        </p>
        <Composer onListening={setInterim} listenSignal={micSignal} />
      </footer>
    </main>
  );
}
